import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toTitleCase } from "../../utils/formatText";
import type { PricingOrg, PricingPlan } from "../../zodios/apiDefinitions/pricing";
import TrellisNavLink from "../../components/core/basic/NavLink";
import type { TSelector } from "../../i18n/types";

export type PlanTypeKey = "Dynamic Price" | "Competitor" | "Rules Engine";

// Backend `template_type` values that surface through getPlanType / rules-engine badges.
// `PROFILE` is a backend alias for `MARGIN_PROFILING`; both share the "Margin Profiling" label.
export type TemplateTypeKey = "CLEARANCE" | "MARGIN_PROFILING" | "PROFILE" | "BSR" | "CUSTOM";

const PLAN_TYPE_SELECTORS: Record<PlanTypeKey, TSelector> = {
  "Dynamic Price": ($) => $.pricingv2["Dynamic Price"],
  Competitor: ($) => $.pricingv2["Competitor"],
  "Rules Engine": ($) => $.pricingv2["Rules Engine"],
};

const TEMPLATE_TYPE_SELECTORS: Record<TemplateTypeKey, TSelector> = {
  CLEARANCE: ($) => $.pricingv2["Clearance"],
  MARGIN_PROFILING: ($) => $.pricingv2["Margin Profiling"],
  PROFILE: ($) => $.pricingv2["Margin Profiling"],
  BSR: ($) => $.pricingv2["BSR"],
  CUSTOM: ($) => $.pricingv2["Custom"],
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type TFn = TFunction<any, any>;

export const getPlanTypeLabel = (t: TFn, value: string): string => {
  if (value in PLAN_TYPE_SELECTORS) return t(PLAN_TYPE_SELECTORS[value as PlanTypeKey]);
  if (value in TEMPLATE_TYPE_SELECTORS) return t(TEMPLATE_TYPE_SELECTORS[value as TemplateTypeKey]);
  return value;
};

// `null` template_type is treated as CUSTOM (see PricingPlanTable.tsx call site).
export const getTemplateTypeLabel = (t: TFn, templateType: string | null | undefined): string => {
  if (templateType == null) return t(TEMPLATE_TYPE_SELECTORS.CUSTOM);
  if (templateType in TEMPLATE_TYPE_SELECTORS) return t(TEMPLATE_TYPE_SELECTORS[templateType as TemplateTypeKey]);
  return templateType;
};

// English labels (also serve as i18n keys in pricingv2 namespace).
export const SEGMENT_TEXT: Record<string, string> = {
  sales_pressure: "Sales Pressure",
  profit_potential: "Profit Potential",
  right_price: "Right Price",
  out_of_stock: "Out of Stock",
  slow_movers: "Slow Movers",
  no_price: "Slow Movers",
  new_plan: "Profiling",
};

// `DAYS_ON_HAND_LABELS` keys are user-facing labels; values are panda-css palette color names.
export const DAYS_ON_HAND_LABELS = {
  Deficient: "fuchsia",
  Healthy: "green",
  Excess: "yellow",
  Overstock: "mauve",
};

// English labels keyed by status enum (also serve as i18n keys in pricingv2 namespace).
export const PRICING_STATUS_LABELS = {
  enabled: "Enrolled",
  disabled: "Paused",
  not_enrolled: "Not Enrolled",
};

export const BUCKET_COLORS: Record<string, string> = {
  "No Forecast": "rgba(46,224,202,0.55)",
  "No Forecast Price": "rgba(145,232,225,0.55)",
  Steady: "rgba(44,175,254,0.55)",
  Overforecast: "rgba(254,106,53,0.55)",
  Underforecast: "rgba(0,226,114,0.55)",
  Opportunity: "rgba(254,181,106,0.55)",
  "No Sales": "rgba(107,138,188,0.55)",
  Monitor: "rgba(213,104,251,0.55)",
  Recovery: "rgba(250,75,66,0.55)",
  Scaling: "rgba(84,79,197,0.55)",
  Rescue: "rgba(227, 121, 179, 0.55)",
  Good: "hsla(226, 43.10%, 38.60%, 0.55)",
};

const types: ReadonlyArray<{ value: string; label: PlanTypeKey; description: string }> = [
  {
    value: "same_strategy",
    label: "Dynamic Price",
    description: "Same strategy for all products",
  },
  {
    value: "same_price",
    label: "Dynamic Price",
    description: "Same price for all products",
  },
  {
    value: "competitor",
    label: "Competitor",
    description: "Competitor Matching",
  },
  {
    value: "rules_engine",
    label: "Rules Engine",
    description: "Custom rules for the pricing plan",
  },
] as const;

export const getPlanType = (pricingPlan: {
  plan_type: string;
  is_auto: boolean;
  current_program?: { template_type: string };
}): PlanTypeKey | TemplateTypeKey => {
  if (pricingPlan.plan_type === "rules_engine") {
    if (pricingPlan.is_auto) {
      const templateType = pricingPlan.current_program?.template_type ?? null;
      if (
        templateType != null &&
        templateType in TEMPLATE_TYPE_SELECTORS &&
        templateType !== "MARGIN_PROFILING" &&
        templateType !== "PROFILE"
      ) {
        return templateType as TemplateTypeKey;
      }

      return types[1]!.label;
    }
  }

  let planType = types[1]!;

  for (let i = 0; i < types.length; i++) {
    const pt = types[i]!;
    if (pt.value === pricingPlan.plan_type) {
      planType = pt;
      break;
    }
  }

  return planType.label;
};

export const PricingRulesNav = ({ pricingPlan, hasModule }: { pricingPlan: PricingPlan; hasModule?: boolean }) => {
  const { t } = useTranslation(["pricingv2"]);
  if (!hasModule && pricingPlan.plan_type === "rules_engine") {
    if (pricingPlan.is_auto) {
      return null;
    }
  }

  let name: string = t(($) => $.pricingv2["Pricing Rules"]);
  if (hasModule && pricingPlan.is_auto) {
    name = t(($) => $.pricingv2["Pricing Rules (Debug)"]);
  }

  return <TrellisNavLink eventKey="rules_engine">{name}</TrellisNavLink>;
};

const calcInventoryTarget = (
  rules: Exclude<PricingOrg["supply_target_program"], null>["rules"],
  inventoryOnHand: number,
  desiredInventoryLevel: number,
) => {
  for (let i = 0; i < rules.length; i++) {
    const minLevel = (rules[i]!.min / 100) * desiredInventoryLevel;
    const maxLevel = (rules[i]!.max / 100) * desiredInventoryLevel;

    if (inventoryOnHand >= minLevel && inventoryOnHand <= maxLevel) {
      return rules[i]!["status"];
    }
  }

  return "N/A";
};

export const getInventoryStatus = (pricingPlan: PricingPlan | null) => {
  if (!pricingPlan?.override_supply) {
    return null;
  }

  if (!pricingPlan.pricing_org.supply_target_program?.rules) {
    if (pricingPlan.days_of_supply < (pricingPlan.target_supply ?? 0)) {
      return "Deficient";
    }

    return "Healthy";
  }

  const inventoryTarget = calcInventoryTarget(
    pricingPlan.pricing_org.supply_target_program.rules,
    pricingPlan.days_of_supply,
    pricingPlan.target_supply ?? 0,
  );
  return toTitleCase(inventoryTarget);
};

/**
 * Consider `offsetType` as "relative vs absolute" more than "currency vs percent".
 * Most offsets are applied to currency fields, hence the names, but if it's applied to, say ACOS, that's when "currency" should be read as "absolute".
 */
export const applyOffset = (value: number, offsetAmount: number, offsetType: "currency" | "percent") => {
  if (offsetType === "currency") {
    return value + offsetAmount;
  }

  return value + value * (offsetAmount / 100);
};

const getForecastBucketName = (bucket: string) => {
  return bucket;
};

const useForecastBucketLabel = () => {
  const { t } = useTranslation(["pricingv2"]);
  return (bucket: string): string => {
    switch (bucket) {
      case "No Forecast":
        return t(($) => $.pricingv2.forecast_bucket["No Forecast"]);
      case "No Forecast Price":
        return t(($) => $.pricingv2.forecast_bucket["No Forecast Price"]);
      case "Steady":
        return t(($) => $.pricingv2.forecast_bucket["Steady"]);
      case "Overforecast":
        return t(($) => $.pricingv2.forecast_bucket["Overforecast"]);
      case "Underforecast":
        return t(($) => $.pricingv2.forecast_bucket["Underforecast"]);
      case "Opportunity":
        return t(($) => $.pricingv2.forecast_bucket["Opportunity"]);
      case "No Sales":
        return t(($) => $.pricingv2.forecast_bucket["No Sales"]);
      case "Monitor":
        return t(($) => $.pricingv2.forecast_bucket["Monitor"]);
      case "Recovery":
        return t(($) => $.pricingv2.forecast_bucket["Recovery"]);
      case "Scaling":
        return t(($) => $.pricingv2.forecast_bucket["Scaling"]);
      case "Rescue":
        return t(($) => $.pricingv2.forecast_bucket["Rescue"]);
      case "Good":
        return t(($) => $.pricingv2.forecast_bucket["Good"]);
      default:
        return bucket;
    }
  };
};

export const ForecastBadge = ({ bucket }: { bucket: string }): React.JSX.Element => {
  const translateBucket = useForecastBucketLabel();
  const bucketName = getForecastBucketName(bucket);
  return (
    <span
      className="badge rounded-pill me-3"
      style={{
        backgroundColor: BUCKET_COLORS[bucketName]?.replace("0.55", "0.75") || "#ccc",
        color: "#fff",
      }}
    >
      {translateBucket(bucketName)}
    </span>
  );
};
